/**
 * Data Explorer — examine a small sample of untyped, nested records on one screen.
 *
 * Owns all explorer state and wires the pieces together: toolbar, filter chips, the
 * virtualized grid, and the Profile / Record / Columns side panel. Per-sample state is
 * reset by keying the session on `sampleKey`; the user's saved default view (and any
 * unsaved layout from this browser session) seeds each new sample.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActiveFilterBar, useTheme, spacing, fontSize } from '@fti/design-system';
import type { DataExplorerProps, ExplorerGridHandle } from './contracts';
import type { AppliedView, ColumnLayout, Density, Filter, NewColumnsPolicy, Row, Schema, SortState, ViewConfig } from './model/types';
import { inferSchema } from './model/flatten';
import { filterLabel } from './model/filter';
import { profileColumn } from './model/profile';
import { visibleColumnDefs } from './model/layout';
import { applyViewConfig, builtInDefault, isDirty, toViewConfig } from './model/viewConfig';
import { downloadText, toCsv, toJson } from './model/exportData';
import { useViewConfig } from './hooks/useViewConfig';
import { useExplorerData } from './hooks/useExplorerState';
import { ExplorerToolbar } from './ExplorerToolbar';
import { ExplorerGrid } from './ExplorerGrid';
import { ColumnFilterPopover } from './ColumnFilterPopover';
import { SidePanel, type SidePanelTab } from './SidePanel';
import { ColumnProfilePanel } from './ColumnProfilePanel';
import { RecordDetailPanel } from './RecordDetailPanel';
import { ColumnManager } from './ColumnManager';
import { ViewConfigMenu } from './ViewConfigMenu';

type ViewStore = ReturnType<typeof useViewConfig>;

export function DataExplorer(props: DataExplorerProps) {
  const view = useViewConfig(props.scope);
  const schema = useMemo(() => inferSchema(props.rows), [props.rows]);
  return <ExplorerSession key={props.sampleKey} {...props} schema={schema} view={view} />;
}

/** The view a new sample opens in: this session's layout → saved default → built-in. */
function initialView(schema: Schema, view: ViewStore, scope: string, defaultPinned: string[]): AppliedView {
  if (view.sessionLayout) {
    return applyViewConfig(
      {
        version: 1,
        scope,
        savedAt: new Date().toISOString(),
        layout: view.sessionLayout.layout,
        density: view.sessionLayout.density,
        newColumns: view.saved?.newColumns ?? 'show',
        filters: view.saved?.filters,
      },
      schema,
    );
  }
  return applyViewConfig(view.saved ?? builtInDefault(schema, { defaultPinned, scope }), schema);
}

function timestamp(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}`;
}

interface FilterTarget {
  columnId: string;
  anchor: HTMLElement;
  filterId?: string;
}

function ExplorerSession({
  rows,
  totalCount,
  scope,
  defaultPinned = [],
  exportName = 'explorer',
  schema,
  view,
}: DataExplorerProps & { schema: Schema; view: ViewStore }) {
  const { theme } = useTheme();
  const gridRef = useRef<ExplorerGridHandle>(null);

  const [initial] = useState(() => initialView(schema, view, scope, defaultPinned));
  const [layout, setLayout] = useState<ColumnLayout>(initial.layout);
  const [density, setDensity] = useState<Density>(initial.density);
  const [newColumns, setNewColumns] = useState<NewColumnsPolicy>(initial.newColumns);
  const [filters, setFilters] = useState<Filter[]>(initial.filters);
  const [notices, setNotices] = useState<string[]>(initial.notices);
  const [quickSearch, setQuickSearch] = useState('');
  const [sort, setSort] = useState<SortState>(null);
  const [selectedColumn, setSelectedColumn] = useState<string | null>(null);
  const [selectedRow, setSelectedRow] = useState<number | null>(null);
  const [panel, setPanel] = useState<SidePanelTab | null>(null);
  const [filterTarget, setFilterTarget] = useState<FilterTarget | null>(null);
  const [highlighted, setHighlighted] = useState<string | null>(null);

  const { indices, columns, summaries, filteredColumns } = useExplorerData({
    rows,
    schema,
    layout,
    filters,
    quickSearch,
    sort,
  });

  // ---------------------------------------------------------------------------
  // Default view: baseline for the "Modified" indicator, and session persistence
  // ---------------------------------------------------------------------------

  const baseline = useMemo(
    () => applyViewConfig(view.saved ?? builtInDefault(schema, { defaultPinned, scope }), schema),
    // defaultPinned is usually an inline literal; compare by content, not identity.
    [view.saved, schema, scope, defaultPinned.join('|')],
  );
  const includeSavedFilters = Boolean(view.saved?.filters?.length);
  const dirty = isDirty({ layout, density, filters }, baseline, includeSavedFilters);

  // Unsaved arrangement survives new previews within this browser session (FR-022).
  const { saveSession, clearSession } = view;
  useEffect(() => {
    if (isDirty({ layout, density, filters: [] }, { ...baseline, filters: [] }, false)) {
      saveSession({ layout, density });
    } else {
      clearSession();
    }
  }, [layout, density, baseline, saveSession, clearSession]);

  const applyView = useCallback((applied: AppliedView, withFilters: boolean) => {
    setLayout(applied.layout);
    setDensity(applied.density);
    setNewColumns(applied.newColumns);
    if (withFilters) setFilters(applied.filters);
    setNotices(applied.notices);
  }, []);

  const handleSave = useCallback(
    (includeFilters: boolean): string => {
      const cfg: ViewConfig = toViewConfig({ layout, density, newColumns, filters }, scope, includeFilters);
      view.save(cfg);
      view.clearSession();
      const shown = visibleColumnDefs(schema, layout).length;
      const parts = [`${shown} columns`, `${layout.pinned.length} pinned`];
      if (cfg.filters?.length) parts.push(`${cfg.filters.length} filter${cfg.filters.length === 1 ? '' : 's'}`);
      return `Saved: ${parts.join(', ')}`;
    },
    [layout, density, newColumns, filters, scope, view, schema],
  );

  const handleRevert = useCallback(() => {
    applyView(baseline, includeSavedFilters);
    view.clearSession();
  }, [applyView, baseline, includeSavedFilters, view]);

  const handleReset = useCallback(() => {
    view.clear();
    view.clearSession();
    applyView(applyViewConfig(builtInDefault(schema, { defaultPinned, scope }), schema), true);
  }, [view, applyView, schema, defaultPinned, scope]);

  const handleExportView = useCallback(() => {
    view.exportFile(toViewConfig({ layout, density, newColumns, filters }, scope, filters.length > 0));
  }, [view, layout, density, newColumns, filters, scope]);

  const handleImportView = useCallback(
    async (file: File) => {
      const cfg = await view.importFile(file);
      applyView(applyViewConfig(cfg, schema), true);
      view.clearSession();
    },
    [view, applyView, schema],
  );

  // ---------------------------------------------------------------------------
  // Grid interactions
  // ---------------------------------------------------------------------------

  const handleSort = useCallback((columnId: string) => {
    setSort((s) => {
      if (!s || s.column !== columnId) return { column: columnId, direction: 'asc' };
      if (s.direction === 'asc') return { column: columnId, direction: 'desc' };
      return null;
    });
  }, []);

  const handleOpenFilter = useCallback(
    (columnId: string, anchor: HTMLElement) => {
      const existing = filters.find((f) => f.column === columnId);
      setFilterTarget({ columnId, anchor, filterId: existing?.id });
    },
    [filters],
  );

  const handleSelectColumn = useCallback((columnId: string) => {
    setSelectedColumn(columnId);
    setPanel('profile');
  }, []);

  const handleSelectRow = useCallback((rowIndex: number) => {
    setSelectedRow(rowIndex);
    setPanel('record');
  }, []);

  const handleResize = useCallback((columnId: string, width: number) => {
    setLayout((l) => ({ ...l, widths: { ...l.widths, [columnId]: width } }));
  }, []);

  const handleExpandGroup = useCallback((groupId: string) => {
    setLayout((l) => ({ ...l, collapsedGroups: l.collapsedGroups.filter((g) => g !== groupId) }));
  }, []);

  const highlightTimer = useRef<ReturnType<typeof setTimeout>>();
  const handleJumpTo = useCallback(
    (columnId: string) => {
      // Make sure the column is actually rendered before scrolling to it.
      setLayout((l) => ({
        ...l,
        hidden: l.hidden.filter((h) => h !== columnId),
        collapsedGroups: l.collapsedGroups.filter((g) => !(columnId === g || columnId.startsWith(`${g}.`))),
      }));
      setTimeout(() => gridRef.current?.scrollToColumn(columnId), 0);
      setHighlighted(columnId);
      clearTimeout(highlightTimer.current);
      highlightTimer.current = setTimeout(() => setHighlighted(null), 1500);
    },
    [],
  );
  useEffect(() => () => clearTimeout(highlightTimer.current), []);

  const addFilter = useCallback((f: Filter) => {
    setFilters((fs) => {
      const i = fs.findIndex((x) => x.id === f.id);
      if (i === -1) return [...fs, f];
      const next = fs.slice();
      next[i] = f;
      return next;
    });
  }, []);

  // ---------------------------------------------------------------------------
  // Record navigation + keyboard
  // ---------------------------------------------------------------------------

  const position = selectedRow === null ? -1 : indices.indexOf(selectedRow);
  const stepRecord = useCallback(
    (delta: number) => {
      if (position === -1) return;
      const next = indices[position + delta];
      if (next === undefined) return;
      setSelectedRow(next);
      gridRef.current?.scrollToRow(next);
    },
    [indices, position],
  );

  useEffect(() => {
    if (!panel) return;
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
      if (filterTarget) return; // the popover owns Escape while open
      if (e.key === 'Escape') setPanel(null);
      else if (panel === 'record' && e.key === '[') stepRecord(-1);
      else if (panel === 'record' && e.key === ']') stepRecord(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [panel, filterTarget, stepRecord]);

  // ---------------------------------------------------------------------------
  // Export
  // ---------------------------------------------------------------------------

  const handleExport = useCallback(
    (format: 'csv' | 'json') => {
      const name = `${exportName}-${timestamp()}`;
      if (format === 'csv') downloadText(`${name}.csv`, toCsv(rows, indices, visibleColumnDefs(schema, layout)), 'text/csv;charset=utf-8');
      else downloadText(`${name}.json`, toJson(rows, indices), 'application/json');
    },
    [exportName, rows, indices, schema, layout],
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const filterColumn = filterTarget ? schema.byId.get(filterTarget.columnId) : undefined;
  const profileCol = selectedColumn ? schema.byId.get(selectedColumn) : undefined;
  const profile = useMemo(
    () => (profileCol ? profileColumn(rows, indices, profileCol) : null),
    [profileCol, rows, indices],
  );
  const selectedRecord: Row | undefined = selectedRow === null ? undefined : rows[selectedRow];
  const visibleCount = visibleColumnDefs(schema, layout).length;
  const muted = theme.colors.text.secondary;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
      <ExplorerToolbar
        shownRows={indices.length}
        loadedRows={rows.length}
        visibleColumns={visibleCount}
        totalColumns={schema.columns.length}
        totalCount={totalCount}
        quickSearch={quickSearch}
        onQuickSearch={setQuickSearch}
        density={density}
        onDensity={setDensity}
        onOpenColumns={() => setPanel((p) => (p === 'columns' ? null : 'columns'))}
        onExport={handleExport}
        viewMenu={
          <ViewConfigMenu
            isDirty={dirty}
            hasSaved={view.saved !== null}
            activeFilterCount={filters.length}
            newColumns={newColumns}
            onNewColumnsChange={setNewColumns}
            onSave={handleSave}
            onRevert={handleRevert}
            onReset={handleReset}
            onExport={handleExportView}
            onImport={handleImportView}
          />
        }
      />

      {/* Edit a filter from its column header's filter button; chips only remove. */}
      {filters.length > 0 && (
        <ActiveFilterBar
          size="sm"
          filters={filters.map((f) => ({ value: f.id, label: filterLabel(f) }))}
          onRemove={(id) => setFilters((fs) => fs.filter((f) => f.id !== id))}
          onClearAll={() => setFilters([])}
        />
      )}

      {notices.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, fontSize: fontSize.xs, color: muted }}>
          <span>{notices.join(' ')}</span>
          <button
            type="button"
            onClick={() => setNotices([])}
            style={{ border: 'none', background: 'transparent', color: theme.colors.primary[600], cursor: 'pointer', fontSize: fontSize.xs }}
          >
            Dismiss
          </button>
        </div>
      )}

      <div
        style={{
          display: 'flex',
          height: 'max(480px, calc(100vh - 180px))',
          border: `1px solid ${theme.colors.border.default}`,
          borderRadius: '6px',
          overflow: 'hidden',
        }}
      >
        <div style={{ flex: 1, minWidth: 0, height: '100%' }}>
          <ExplorerGrid
            ref={gridRef}
            rows={rows}
            indices={indices}
            columns={columns}
            density={density}
            sort={sort}
            summaries={summaries}
            filteredColumns={filteredColumns}
            selectedColumn={selectedColumn}
            selectedRow={selectedRow}
            highlightedColumn={highlighted}
            onSort={handleSort}
            onOpenFilter={handleOpenFilter}
            onSelectColumn={handleSelectColumn}
            onSelectRow={handleSelectRow}
            onResize={handleResize}
            onExpandGroup={handleExpandGroup}
          />
        </div>
        {panel && (
          <SidePanel
            tab={panel}
            onTabChange={setPanel}
            onClose={() => setPanel(null)}
            profile={
              profileCol && profile ? (
                <ColumnProfilePanel column={profileCol} profile={profile} onAddFilter={addFilter} />
              ) : (
                <span style={{ color: muted, fontSize: fontSize.sm }}>Click a column name to profile it.</span>
              )
            }
            record={
              selectedRecord ? (
                <RecordDetailPanel
                  row={selectedRecord}
                  position={position + 1}
                  total={indices.length}
                  hasPrev={position > 0}
                  hasNext={position !== -1 && position < indices.length - 1}
                  onPrev={() => stepRecord(-1)}
                  onNext={() => stepRecord(1)}
                />
              ) : (
                <span style={{ color: muted, fontSize: fontSize.sm }}>
                  Click a row number to open the full record.
                </span>
              )
            }
            columns={
              <ColumnManager schema={schema} layout={layout} summaries={summaries} onChange={setLayout} onJumpTo={handleJumpTo} />
            }
          />
        )}
      </div>

      {filterTarget && filterColumn && (
        <ColumnFilterPopover
          column={filterColumn}
          rows={rows}
          indices={indices}
          existing={filters.find((f) => f.id === filterTarget.filterId)}
          anchor={filterTarget.anchor}
          onApply={(f) => {
            addFilter(f);
            setFilterTarget(null);
          }}
          onRemove={() => {
            setFilters((fs) => fs.filter((f) => f.id !== filterTarget.filterId));
            setFilterTarget(null);
          }}
          onClose={() => setFilterTarget(null)}
        />
      )}
    </div>
  );
}
